import { MAIN_NAV_ORDER } from 'components/navigation/Sidebar/mainNavOrder'
// Labels only, from a JSX-free module. Importing SettingsPage itself for these two arrays
// created docs → categoryOrder → SettingsPage → hooks → … and blanked the whole app with
// `Cannot access 'TEXT_ZOOM_LEVELS' before initialization`. See settingsNavOrder.ts.
import {
  COMPANY_NAV_ORDER,
  PERSONAL_NAV_ORDER,
} from 'pages/settings/SettingsPage/settingsNavOrder'

import type { DocCategory } from './catalog'

/**
 * Orders the CUSTOMER docs index the way a reader already understands the app (VRG-227):
 *
 *   1. the sidebar's main nav, top to bottom (`MAIN_NAV_ORDER`)
 *   2. then Settings' own order — Personal, then Company (`PERSONAL_NAV_ORDER`,
 *      `COMPANY_NAV_ORDER`)
 *
 * Both come from the module that OWNS each order rather than a hand-typed snapshot, so
 * this cannot silently drift the way a second copy would: reorder the sidebar or a
 * Settings section and the docs index follows without anyone touching this file, UNLESS
 * the reorder also changes which nav item a category maps to below — a real editorial
 * decision, and exactly what `DOC_CATEGORY_NAV_REF` exists to hold in ONE place.
 *
 * ⚠️ This file must import ORDER, never a page. It briefly read the two arrays off
 * `SettingsPage.tsx` directly — free in bundle terms, since both are eagerly imported
 * anyway, but it added a module-graph edge:
 *
 *   docs/index.ts → categoryOrder.ts → SettingsPage.tsx → hooks barrel → …
 *
 * That cycle left `TEXT_ZOOM_LEVELS` in its temporal dead zone when `TextSizeCard.tsx`
 * evaluated it at module top level, and the entire app rendered blank with no console
 * error — the failure only surfaces if you import `App.tsx` by hand and read the
 * rejection. Keep the dependency pointed at `settingsNavOrder.ts`, which is JSX-free and
 * imports nothing but `ROUTES`.
 */

type NavGroup = 'main' | 'personal' | 'company'

/** Which nav item each closed-set customer category (publish-guides.mjs `CATEGORIES`)
 *  corresponds to. Every id in that set must have an entry here — see `rankOf` for what
 *  happens when one doesn't. */
const DOC_CATEGORY_NAV_REF: Record<string, { group: NavGroup; key: string }> = {
  // The sidebar has no "Getting started" row of its own — Home is the closest thing to
  // it (the landing page, and the guide this category leads with), so it inherits Home's
  // position: first.
  'getting-started': { group: 'main', key: 'Home' },
  certificates: { group: 'main', key: 'Certificates' },
  products: { group: 'main', key: 'Products' },
  tags: { group: 'main', key: 'Tags' },
  analytics: { group: 'main', key: 'Analytics' },
  // The two settings categories are coarser than any single Settings section — they ARE
  // the Personal/Company halves — so each takes the position of the FIRST section in its
  // half, which is exactly where a reader lands on that tab.
  'personal-settings': { group: 'personal', key: PERSONAL_NAV_ORDER[0] },
  'company-settings': { group: 'company', key: COMPANY_NAV_ORDER[0] },
  // Integrations is its own doc category (installation, field mapping, integrations) but
  // maps to a single real Company section — "Integrations" — so it sorts at THAT
  // section's actual position among the rest of Company, rather than tying with the
  // company-settings bucket above.
  integrations: { group: 'company', key: 'Integrations' },
}

const GROUP_RANK: Record<NavGroup, number> = { main: 0, personal: 1, company: 2 }

// `readonly`, because the two settings orders are declared `readonly string[]` in
// settingsNavOrder.ts and nothing here mutates any of them — only `.indexOf`.
const GROUP_ORDER: Record<NavGroup, readonly string[]> = {
  main: MAIN_NAV_ORDER.map(item => item.title),
  personal: PERSONAL_NAV_ORDER,
  company: COMPANY_NAV_ORDER,
}

/**
 * A category with no entry above, or whose entry points at a nav item that no longer
 * exists, sorts LAST rather than vanishing from the index — and logs loudly in dev so
 * the gap gets noticed rather than quietly tolerated. The closed `CATEGORIES` set in
 * publish-guides.mjs is small (8 ids today), so every one of them should always resolve.
 */
const rankOf = (id: string): [number, number] => {
  const ref = DOC_CATEGORY_NAV_REF[id]
  if (!ref || !ref.key) {
    if (import.meta.env.DEV) {
      console.error(
        `[docs] category '${id}' has no entry in DOC_CATEGORY_NAV_REF ` +
          `(apps/platform/src/docs/categoryOrder.ts) — sorting it last instead of dropping ` +
          `it. Map it to a sidebar or Settings item.`,
      )
    }
    return [Number.MAX_SAFE_INTEGER, 0]
  }
  const withinGroup = GROUP_ORDER[ref.group].indexOf(ref.key)
  if (withinGroup === -1) {
    if (import.meta.env.DEV) {
      console.error(
        `[docs] category '${id}' maps to '${ref.group}' item '${ref.key}', which no longer ` +
          `exists there — sorting it last. Update DOC_CATEGORY_NAV_REF in ` +
          `apps/platform/src/docs/categoryOrder.ts.`,
      )
    }
    return [Number.MAX_SAFE_INTEGER, 0]
  }
  return [GROUP_RANK[ref.group], withinGroup]
}

/**
 * Sort the docs categories into "sidebar order, then Settings order". Pure and stable —
 * ties (there are none today; every mapped key is unique) keep their manifest order.
 */
export const sortDocCategories = (categories: DocCategory[]): DocCategory[] => {
  const ranked = categories.map((category, index) => ({ category, index, rank: rankOf(category.id) }))
  ranked.sort((a, b) => a.rank[0] - b.rank[0] || a.rank[1] - b.rank[1] || a.index - b.index)
  return ranked.map(r => r.category)
}
